import React, { useEffect, useState } from 'react';
import { Checkbox, Form, Input, Modal, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

export interface ReasonPromptResult {
  reason_code?: string;
  notes?: string;
  notify_candidate?: boolean;
}

interface Props {
  open: boolean;
  title: string;
  okText?: string;
  /** When set, a reason from the rejection-reason master is required. */
  appliesTo?: 'Application' | 'Requisition' | 'Offer';
  /** Free-text reason only (withdraw, cancel, blacklist). */
  notesRequired?: boolean;
  notesLabel?: string;
  showNotify?: boolean;
  danger?: boolean;
  onCancel: () => void;
  onSubmit: (v: ReasonPromptResult) => Promise<void> | void;
}

/** Confirmation popup for status actions that need a reason (allowed as a popup per HRMS UI rules). */
const ReasonPrompt: React.FC<Props> = ({
  open,
  title,
  okText = 'Confirm',
  appliesTo,
  notesRequired,
  notesLabel = 'Notes',
  showNotify,
  danger = true,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<ReasonPromptResult>();
  const [busy, setBusy] = useState(false);
  const { data: reasons = [] } = useQuery({
    queryKey: hrmsKeys.rejectionReasons(appliesTo),
    queryFn: () => hrmsService.listRejectionReasons({ applies_to: appliesTo }),
    enabled: open && Boolean(appliesTo),
  });

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const ok = async () => {
    const v = await form.validateFields();
    setBusy(true);
    try {
      await onSubmit(v);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      okButtonProps={{ danger, loading: busy }}
      onOk={ok}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ notify_candidate: false }}>
        {appliesTo && (
          <Form.Item name="reason_code" label="Reason" rules={[{ required: true, message: 'Select a reason' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select reason"
              options={reasons.map((r: any) => ({ value: r.code, label: r.name }))}
            />
          </Form.Item>
        )}
        <Form.Item
          name="notes"
          label={notesLabel}
          rules={notesRequired ? [{ required: true, whitespace: true, message: `${notesLabel} is required` }] : []}
        >
          <Input.TextArea rows={3} maxLength={1000} showCount />
        </Form.Item>
        {showNotify && (
          <Form.Item name="notify_candidate" valuePropName="checked">
            <Checkbox>Email the candidate (uses the "Application rejected" template)</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ReasonPrompt;
